/**
 * The page side of the automatic matrix fill (Jim, 2026-10-29: "the matrix
 * should be filled in from the protocol but it should be updatable by the
 * user"): when the fill runs, how its result joins what the person already has
 * on screen, and what a filled tick says on hover.
 *
 * The merge and Save rules themselves live with the server
 * (functions/src/schedule/visitMappingFill.ts), so the page and the server
 * cannot disagree about what a person's edit means.
 *
 * Pure. No React, no Firestore.
 */
import {
  cellsOfMapping,
  fillSignature,
  isFillStale,
  reconcileOnSave,
} from '../../../../functions/src/schedule/visitMappingFill';

import {
  asNeededOf,
  reconcileAsNeededOnSave,
  type AsNeededBasis,
  type AsNeededSource,
} from '../../../../functions/src/schedule/asNeededForms';

export { fillSignature, isFillStale, reconcileOnSave, reconcileAsNeededOnSave };
export type { AsNeededSource, AsNeededBasis };

/** visit id -> form ids ticked in a saved mapping. */
export function cellsOf(mapping: { visits?: unknown } | null | undefined): Record<string, string[]> {
  return cellsOfMapping(mapping);
}

/**
 * Whether the page should call the fill now. At most once per signature in one
 * open of the page, whatever re-renders happen meanwhile: the fill is cheap to
 * repeat on the server but the model call behind it is not free.
 */
export function shouldRunAutoFill(input: {
  stale: boolean;
  visitCount: number;
  formCount: number;
  startedFor: string | null;
  signature: string;
  loading: boolean;
}): boolean {
  if (input.loading || !input.stale) return false;
  if (input.visitCount <= 0 || input.formCount <= 0) return false;
  return input.startedFor !== input.signature;
}

export type FillAdoption = 'adopt' | 'reload' | 'keep' | 'none';

/**
 * What the page does with a fill's result, by the mapping versions involved.
 *
 * `held` is the version of the copy the page shows; `read` is the version the
 * fill READ before writing; `written` the version it left. A fill only adds
 * cells, so the page may take on its result (and claim its version) ONLY when
 * the fill was based on the very version the page holds. If the mapping had
 * moved under the page (another tab saved, a person cleared a cell), taking the
 * newer version would let this page's next Save overwrite that change, so:
 *   - reload   the grid from the server, when nothing on screen is unsaved;
 *   - keep     the old version and the screen as it is, when something is, so
 *              the next Save is refused as stale and says so.
 * A result without versions (nothing to fill) changes nothing.
 */
export function decideFillAdoption(input: {
  held: number;
  read: number | undefined;
  written: number | undefined;
  unsaved: boolean;
}): FillAdoption {
  if (typeof input.read !== 'number' || typeof input.written !== 'number') return 'none';
  if (input.read !== input.held) return input.unsaved ? 'keep' : 'reload';
  return input.written === input.read ? 'none' : 'adopt';
}

/**
 * Join a fill's result into the grid on screen. Only cells the server ADDED
 * since the last saved copy are added; everything else on screen stays as the
 * person left it, including a tick they removed and have not saved yet.
 */
export function joinServerFill(
  inMemory: Map<string, Set<string>>,
  previousSaved: Map<string, Set<string>>,
  server: Map<string, Set<string>>,
): Map<string, Set<string>> {
  const out = new Map<string, Set<string>>();
  inMemory.forEach((set, formId) => out.set(formId, new Set(set)));
  server.forEach((visitIds, formId) => {
    const before = previousSaved.get(formId);
    visitIds.forEach((visitId) => {
      if (before?.has(visitId)) return;
      if (!out.has(formId)) out.set(formId, new Set());
      out.get(formId)!.add(visitId);
    });
  });
  return out;
}

export interface FillTip {
  text: string;
  /**
   * 'model' for a form the model chose, 'protocol' for a label the strict
   * matcher placed, 'standard' for a form the protocol never schedules that the
   * standard rule placed (functions/src/schedule/standardVisitForms.ts).
   */
  kind: 'model' | 'protocol' | 'standard';
}

interface TipVisit {
  formMatches?: unknown;
  modelMatches?: unknown;
}

const norm = (s: string) => s.normalize('NFKC').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim();

/**
 * What an automatically filled tick says on hover, or null when nothing is
 * recorded. `opts.standard` is set when the standard rule placed the cell, and
 * names where the rule puts that form.
 */
export function autoFillTip(
  visit: TipVisit,
  form: { id: string; name: string },
  opts?: { standard?: 'first' | 'after_first' },
): FillTip | null {
  if (opts?.standard) {
    const where = opts.standard === 'first' ? 'at the first visit' : 'at every visit after the first';
    return { text: `Standard form the protocol does not schedule: collected ${where}. Click to untick.`, kind: 'standard' };
  }
  for (const m of Array.isArray(visit.modelMatches) ? (visit.modelMatches as Array<Record<string, unknown>>) : []) {
    if (m?.formId === form.id && typeof m.label === 'string' && m.label.trim()) {
      return { text: `Filled from the protocol: "${m.label.trim()}". Click to untick.`, kind: 'model' };
    }
  }
  for (const m of Array.isArray(visit.formMatches) ? (visit.formMatches as Array<Record<string, unknown>>) : []) {
    if (
      typeof m?.form === 'string' &&
      (m.form === form.name || norm(m.form) === norm(form.name)) &&
      typeof m.label === 'string' &&
      m.label.trim()
    ) {
      return { text: `Filled from the protocol: "${m.label.trim()}". Click to untick.`, kind: 'protocol' };
    }
  }
  return null;
}

// ─── "As needed" (product owner, 2026-11-02) ────────────────────────────────
//
// A form tied to an EVENT rather than a visit (serious adverse events, protocol
// deviations, ...) is "as needed": on no visit. The platform proposes it for
// forms left unplaced after the fill; a person can switch it on or off. The
// stored record and its rules live with the server
// (functions/src/schedule/asNeededForms.ts); this is the page's side of it.

type AsNeededMap = Record<string, AsNeededSource>;
type Ticks = Map<string, Set<string>>;

/** The as-needed forms of a saved mapping, as shown on screen: form id -> where it came from. */
export function asNeededOnScreen(mapping: { asNeeded?: unknown } | null | undefined): AsNeededMap {
  return asNeededOf(mapping).asNeeded;
}

/**
 * The person switches a form's "As needed" on or off. On: it becomes theirs, and
 * the form's visit ticks are cleared (as needed means no fixed visit; Save
 * remembers the cleared ticks, so a later fill does not bring them back). Off: it
 * is removed. Pure; the inputs are not changed.
 */
export function toggleAsNeeded(
  asNeeded: AsNeededMap,
  assignments: Ticks,
  formId: string,
): { asNeeded: AsNeededMap; assignments: Ticks } {
  const nextNeeded: AsNeededMap = { ...asNeeded };
  const nextTicks: Ticks = new Map();
  assignments.forEach((set, id) => nextTicks.set(id, new Set(set)));
  if (formId in nextNeeded) {
    delete nextNeeded[formId];
  } else {
    nextNeeded[formId] = 'person';
    nextTicks.set(formId, new Set());
  }
  return { asNeeded: nextNeeded, assignments: nextTicks };
}

/**
 * Ticking a visit for a form clears its "As needed", whoever set it and however
 * the tick came (a click, a whole row or column, a fill). Returns the very same
 * object when nothing changes, so an effect that uses it cannot loop.
 */
export function dropTickedAsNeeded(asNeeded: AsNeededMap, assignments: Ticks): AsNeededMap {
  const ids = Object.keys(asNeeded).filter((id) => (assignments.get(id)?.size ?? 0) > 0);
  if (ids.length === 0) return asNeeded;
  const out = { ...asNeeded };
  for (const id of ids) delete out[id];
  return out;
}

/**
 * Join a fill's result into the as-needed forms on screen. Only what the server
 * CHANGED since the last saved copy joins: a form it newly proposed is added; a
 * proposal it released (the protocol placed the form) is dropped unless the
 * person has already made it theirs. A person's own choice, and one they
 * switched off on screen, stay as they left them.
 */
export function joinServerAsNeeded(
  onScreen: AsNeededMap,
  previousSaved: AsNeededMap,
  server: AsNeededMap,
  assignmentsAfter: Ticks,
): AsNeededMap {
  const out: AsNeededMap = { ...onScreen };
  for (const [id, source] of Object.entries(server)) {
    if (!(id in previousSaved) && !(id in out)) out[id] = source;
  }
  for (const [id, source] of Object.entries(previousSaved)) {
    if (source === 'proposed' && !(id in server) && out[id] === 'proposed') delete out[id];
  }
  return dropTickedAsNeeded(out, assignmentsAfter);
}

/** A comparable string, for unsaved-change tracking. */
export function serializeAsNeeded(asNeeded: AsNeededMap): string {
  return Object.entries(asNeeded)
    .map(([id, source]) => `${id}=${source}`)
    .sort()
    .join('|');
}

const BASIS_TEXT: Record<AsNeededBasis, string> = {
  name: 'its name is that of an event form',
  category: 'its category says it is not tied to a visit',
  model: 'the protocol gives it no visit and its content reads as an event form',
};

/** What the control says on hover, and which look it gets. */
export function asNeededTip(
  source: AsNeededSource,
  basis?: AsNeededBasis,
): { text: string; kind: 'proposed' | 'person' } {
  if (source === 'person') {
    return { text: 'Set as needed by you: collected when an event happens, not at a fixed visit. Click to change.', kind: 'person' };
  }
  const why = basis ? ` (${BASIS_TEXT[basis]})` : '';
  return {
    text: `Proposed as needed${why}: collected when an event happens, not at a fixed visit. Click to change.`,
    kind: 'proposed',
  };
}

/**
 * The line shown when the automatic fill from the protocol did not complete (a
 * stored record, studyDesign/config.matrixFillFailure) and the grid is still
 * not filled for what the study holds now. Null otherwise.
 */
export function fillFailureNotice(
  failure: { reasonText?: unknown; outcome?: unknown } | null | undefined,
  stale: boolean,
): string | null {
  if (!failure || !stale) return null;
  // The plain sentence stored with the failure, never raw error text.
  const why = typeof failure.reasonText === 'string' && failure.reasonText.trim() ? ` (${failure.reasonText.trim().replace(/[.\s]+$/, '')})` : '';
  return `The automatic fill from the protocol did not complete${why}. Use Fill from protocol to try again.`;
}
